import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import SingleDatePicker from "../../../shared/components/DatePicker/SingleDatePicker";
import useApi from "../../../shared/hooks/useApi";
import { queryKeys } from "../../../shared/query-keys";
import { PatientId } from "../../../shared/schema/schema";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

interface Props {
  patientId: PatientId;
  disclosure: ReturnType<typeof useDisclosure>;
}

/**
 * Pushes the patient's current address onto the patient's visits in an optional
 * date range. Both bounds are optional - leaving both empty syncs every visit
 * the patient has.
 */
const PatientSyncVisitsAddressModal = (props: Props) => {
  const { api } = useApi();
  const toast = useToast();

  const [startDate, setStartDate] = React.useState<LocalDate | null>(null);
  const [endDate, setEndDate] = React.useState<LocalDate | null>(null);

  const patientQuery = useQuery({
    queryKey: queryKeys.patient.get(props.patientId),
    queryFn: () => {
      return api.get("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: props.patientId },
      });
    },
    select: (patient) => patient.address?.text ?? null,
  });

  const isInvalidRange = startDate !== null && endDate !== null && startDate.isAfter(endDate);

  const syncVisitsAddress = useMutation({
    mutationFn: () => {
      return api.post("/agencies/:agencyId/patients/:patientId/sync_address_to_visits", {
        path: { patientId: props.patientId },
        body: { startDate, endDate },
      });
    },
    onSuccess: (res) => {
      toast({
        title:
          res.updatedVisitsCount === 0
            ? "No visits were updated"
            : `Updated ${res.updatedVisitsCount} visit${res.updatedVisitsCount === 1 ? "" : "s"}`,
        description:
          res.skippedVisitsCount > 0
            ? `${res.skippedVisitsCount} of the ${res.matchedVisitsCount} visits in range were skipped`
            : `${res.matchedVisitsCount} visits matched the selected range`,
        status: res.updatedVisitsCount === 0 ? "info" : "success",
        position: "top-right",
      });
      window.dispatchEvent(
        new CustomEvent("from-webapp-react", { detail: { type: "refreshVisits" } })
      );
      props.disclosure.onClose();
    },
    onError: (error) => {
      toast({
        title: "Could not synchronize visit addresses",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
      });
    },
  });

  return (
    <Modal size="lg" {...props.disclosure}>
      <ModalOverlay />
      <ModalContent margin="auto">
        <ModalHeader>Synchronize visit addresses</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={5}>
            <Alert status="info">
              <AlertIcon />
              <Text>
                Every visit of this patient in the range below will be given the patient&apos;s
                current address
                {patientQuery.data !== undefined && patientQuery.data !== null ? (
                  <>
                    {" "}
                    (<strong>{patientQuery.data}</strong>)
                  </>
                ) : null}
                .
              </Text>
            </Alert>
            <Flex direction="row" gap={4}>
              <FormControl>
                <FormLabel>From date</FormLabel>
                <SingleDatePicker
                  isClearable={true}
                  placeholderText="No lower limit"
                  selected={startDate}
                  onChange={setStartDate}
                />
                <FormHelperText>
                  Leave empty to include the patient&apos;s past visits
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>To date</FormLabel>
                <SingleDatePicker
                  isClearable={true}
                  placeholderText="No upper limit"
                  selected={endDate}
                  onChange={setEndDate}
                />
                <FormHelperText>Leave empty to include all scheduled future visits</FormHelperText>
              </FormControl>
            </Flex>
            {isInvalidRange && (
              <Alert status="error">
                <AlertIcon />
                The from date must not be after the to date
              </Alert>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.disclosure.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={isInvalidRange}
            isLoading={syncVisitsAddress.isPending}
            onClick={() => syncVisitsAddress.mutate()}
          >
            Synchronize visits
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PatientSyncVisitsAddressModal;
